import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { Camera, Check, Mail, MessageCircle, Smartphone, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { AdminAlert } from "@/components/shell/AdminPage";
import { AvatarCropDialog } from "@/components/profile/AvatarCropDialog";
import { EmailChangePanel } from "@/components/profile/EmailChangePanel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { deleteAvatar, updateDisplayName } from "@/lib/me-api";
import { userAvatarInitial, userDisplayLabel } from "@/lib/user-display";

export function ProfileSection() {
  const { user, setAuthUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [isRemovingAvatar, setIsRemovingAvatar] = useState(false);
  const [confirmRemoveAvatar, setConfirmRemoveAvatar] = useState(false);
  const [emailChangeOpen, setEmailChangeOpen] = useState(false);

  useEffect(() => {
    if (user) setDisplayName(user.display_name?.trim() ?? "");
  }, [user]);

  if (!user) return null;

  const label = userDisplayLabel(user);
  const initial = userAvatarInitial(user);
  const nameDirty = displayName.trim() !== (user.display_name?.trim() ?? "");

  function handlePickAvatar() {
    setConfirmRemoveAvatar(false);
    fileInputRef.current?.click();
  }

  /** 已有头像：点开继续裁剪；无头像：走选文件 */
  function handleAvatarClick() {
    if (confirmRemoveAvatar) return;
    if (user.avatar_url) {
      setCropSrc(user.avatar_url);
      setCropOpen(true);
      return;
    }
    handlePickAvatar();
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("请选择图片文件");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(typeof reader.result === "string" ? reader.result : null);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  }

  async function handleSaveName(event: FormEvent) {
    event.preventDefault();
    setNameError(null);
    setIsSavingName(true);
    try {
      const { user: updated } = await updateDisplayName(displayName.trim());
      setAuthUser(updated);
      toast.success("展示名已保存");
    } catch (error) {
      setNameError(error instanceof ApiError ? error.message : "保存失败，请稍后重试");
    } finally {
      setIsSavingName(false);
    }
  }

  function handlePreviewRemoveAvatar() {
    setConfirmRemoveAvatar(true);
  }

  function handleCancelRemoveAvatar() {
    setConfirmRemoveAvatar(false);
  }

  async function handleConfirmRemoveAvatar() {
    setIsRemovingAvatar(true);
    try {
      const { user: updated } = await deleteAvatar();
      setAuthUser(updated);
      setConfirmRemoveAvatar(false);
      toast.success("已恢复默认头像");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "删除失败，请稍后重试");
    } finally {
      setIsRemovingAvatar(false);
    }
  }

  const previewAvatarUrl = confirmRemoveAvatar ? null : user.avatar_url;

  return (
    <div className="space-y-6">
      <section className="rounded-lg border bg-card p-4">
        <div className="mb-4 space-y-1">
          <h2 className="text-base font-medium">头像</h2>
          <p className="text-sm text-muted-foreground">
            点击头像可继续裁剪；更换则重新选图。侧栏与个人中心同步显示。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={handleAvatarClick}
            disabled={confirmRemoveAvatar}
            className="rounded-full outline-none ring-offset-background transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"
            aria-label={user.avatar_url ? "编辑头像" : "上传头像"}
          >
            <Avatar className="size-16 cursor-pointer">
              {previewAvatarUrl ? <AvatarImage src={previewAvatarUrl} alt={label} /> : null}
              <AvatarFallback className="bg-primary/10 text-lg font-medium text-primary">
                {initial}
              </AvatarFallback>
            </Avatar>
          </button>
          <div className="flex flex-wrap gap-2">
            {confirmRemoveAvatar ? (
              <>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={isRemovingAvatar}
                  onClick={() => void handleConfirmRemoveAvatar()}
                >
                  <Check className="size-4" />
                  {isRemovingAvatar ? "处理中…" : "确认恢复"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isRemovingAvatar}
                  onClick={handleCancelRemoveAvatar}
                >
                  <X className="size-4" />
                  取消
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" size="sm" onClick={handlePickAvatar}>
                  <Camera className="size-4" />
                  更换头像
                </Button>
                {user.avatar_url ? (
                  <Button type="button" variant="ghost" size="sm" onClick={handlePreviewRemoveAvatar}>
                    <Trash2 className="size-4" />
                    恢复默认
                  </Button>
                ) : null}
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <div className="mb-4 space-y-1">
          <h2 className="text-base font-medium">展示名</h2>
          <p className="text-sm text-muted-foreground">在侧栏与成员列表中显示；留空则回退到邮箱或手机。</p>
        </div>
        <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={handleSaveName}>
          <div className="min-w-0 flex-1 space-y-2">
            <Label htmlFor="display-name">展示名</Label>
            <Input
              id="display-name"
              value={displayName}
              maxLength={40}
              placeholder={label}
              onChange={(event) => {
                setNameError(null);
                setDisplayName(event.target.value);
              }}
            />
            {nameError ? <AdminAlert tone="error">{nameError}</AdminAlert> : null}
          </div>
          <Button type="submit" disabled={isSavingName || !nameDirty}>
            {isSavingName ? "保存中…" : "保存"}
          </Button>
        </form>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <div className="mb-4 flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Mail className="size-4" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <h2 className="text-base font-medium">登录邮箱</h2>
            <p className="text-sm text-muted-foreground">
              {user.email ? user.email : "尚未绑定邮箱，暂无法在此改绑"}
            </p>
          </div>
          {user.email && !emailChangeOpen ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setEmailChangeOpen(true)}>
              更换邮箱
            </Button>
          ) : null}
        </div>
        {user.email && emailChangeOpen ? (
          <EmailChangePanel
            currentEmail={user.email}
            onClose={() => setEmailChangeOpen(false)}
          />
        ) : null}
      </section>

      <section className="rounded-lg border border-dashed bg-muted/20 p-4">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Smartphone className="size-4" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-medium">手机号</h2>
            <p className="text-sm text-muted-foreground">
              {user.phone ? `已绑定 ${user.phone}` : "尚未绑定"}
            </p>
            <p className="text-xs text-muted-foreground">手机验证码登录与改绑即将支持。</p>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-dashed bg-muted/20 p-4">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <MessageCircle className="size-4" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-medium">微信</h2>
            <p className="text-sm text-muted-foreground">
              {user.wechat_union_id ? "已关联微信 UnionID" : "尚未绑定微信"}
            </p>
            <p className="text-xs text-muted-foreground">微信授权登录与绑定即将支持，敬请期待。</p>
          </div>
        </div>
      </section>

      <AvatarCropDialog
        open={cropOpen}
        onOpenChange={(open) => {
          setCropOpen(open);
          if (!open) setCropSrc(null);
        }}
        imageSrc={cropSrc}
        onUploaded={setAuthUser}
      />
    </div>
  );
}
